import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import { KbvPluginService } from "@/services/kbvPluginService";
import {
  filterMatchesInDateRange,
  groupMatchesByDate,
  groupMatchesByRound,
  keepRemainingMatches,
  matchDateKey,
  sortDateEntriesDesc,
  sortRoundEntries,
  unwrapBangKeoMatchList,
} from "@/utils/leagueFixturesByLeague";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const BANGKEO_LTD_OPTS = {
  include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
  filterOdds: -1,
  showEmptyOdds: 1,
  sort: "matchTime:asc",
  limit: 50,
};

const MAX_PAGES = 8;
const inflight = new Map();

function withInflight(key, fn) {
  const existing = inflight.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(fn)
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}

async function fetchLtdMatchesPage(leagueId, page) {
  const response = await KbvPluginService.fetchBangKeoMatchesOld({
    ...BANGKEO_LTD_OPTS,
    filterInclude: `leagueId:${leagueId}`,
    page,
  });
  if (response?.error) {
    return { list: [], hasMore: false };
  }
  const list = unwrapBangKeoMatchList(response);
  const total = Number(response?.data?.total);
  const hasMore =
    Number.isFinite(total) && total >= 0
      ? page * BANGKEO_LTD_OPTS.limit < total
      : list.length >= BANGKEO_LTD_OPTS.limit;
  return { list, hasMore };
}

export async function fetchLtdMatchesForLeague(league) {
  const leagueId = league?.id;
  if (leagueId == null || leagueId === "") return [];

  return withInflight(`ltd:${leagueId}:${league?.currentRound ?? ""}`, async () => {
    const all = [];
    let page = 1;
    let hasMore = true;
    while (hasMore && page <= MAX_PAGES) {
      const result = await fetchLtdMatchesPage(leagueId, page);
      all.push(...(result.list || []));
      hasMore = result.hasMore;
      page += 1;
    }
    const remaining = keepRemainingMatches(all, league?.currentRound);
    const map = groupMatchesByRound(remaining);
    return sortRoundEntries(Array.from(map.entries()));
  });
}

async function fetchKetquaMatchesPage(leagueId, page) {
  const response = await KbvPluginService.fetchBangKeoMatchesOld({
    ...BANGKEO_LTD_OPTS,
    filterInclude: `leagueId:${leagueId}`,
    status: "end",
    sort: "matchTime:desc",
    page,
  });
  if (response?.error) {
    return { list: [], hasMore: false };
  }
  const list = unwrapBangKeoMatchList(response);
  const total = Number(response?.data?.total);
  const hasMore =
    Number.isFinite(total) && total >= 0
      ? page * BANGKEO_LTD_OPTS.limit < total
      : list.length >= BANGKEO_LTD_OPTS.limit;
  return { list, hasMore };
}

function keepFinishedRounds(matches, currentRound) {
  if (!Array.isArray(matches) || !matches.length) return [];
  const c = Number(currentRound);
  if (Number.isFinite(c)) {
    return matches.filter((m) => {
      const n = Number(m?.round);
      if (Number.isFinite(n)) return n <= c;
      return true;
    });
  }
  return matches;
}

export async function fetchKetquaMatchesForLeague(league) {
  const leagueId = league?.id;
  if (leagueId == null || leagueId === "") return [];

  return withInflight(`kq:${leagueId}:${league?.currentRound ?? ""}`, async () => {
    const all = [];
    let page = 1;
    let hasMore = true;
    while (hasMore && page <= MAX_PAGES) {
      const result = await fetchKetquaMatchesPage(leagueId, page);
      all.push(...(result.list || []));
      hasMore = result.hasMore;
      page += 1;
    }
    const finished = keepFinishedRounds(all, league?.currentRound);
    const map = groupMatchesByRound(finished);
    return sortRoundEntries(Array.from(map.entries())).reverse();
  });
}

function dateWindow(days, isKetqua) {
  const n = Math.max(1, Number(days) || 30);
  const today = dayjs().tz(TZ);
  if (isKetqua) {
    return {
      fromDate: today.subtract(n - 1, "day").format("YYYY-MM-DD"),
      toDate: today.format("YYYY-MM-DD"),
    };
  }
  return {
    fromDate: today.format("YYYY-MM-DD"),
    toDate: today.add(n - 1, "day").format("YYYY-MM-DD"),
  };
}

async function fetchMatchesPageByDays(leagueId, page, { isKetqua, toDate }) {
  const filterInclude = isKetqua
    ? `leagueId:${leagueId}`
    : `leagueId:${leagueId},toDate:${toDate}`;
  const response = await KbvPluginService.fetchBangKeoMatchesOld({
    ...BANGKEO_LTD_OPTS,
    filterInclude,
    ...(isKetqua ? { status: "end" } : {}),
    sort: "matchTime:desc",
    page,
  });
  if (response?.error) {
    return { list: [], hasMore: false };
  }
  const list = unwrapBangKeoMatchList(response);
  const total = Number(response?.data?.total);
  const hasMore =
    Number.isFinite(total) && total >= 0
      ? page * BANGKEO_LTD_OPTS.limit < total
      : list.length >= BANGKEO_LTD_OPTS.limit;
  return { list, hasMore };
}

function pageStillInWindow(list, fromDate) {
  if (!list?.length) return false;
  let oldest = "";
  for (const m of list) {
    const key = matchDateKey(m);
    if (!key) continue;
    if (!oldest || key < oldest) oldest = key;
  }
  if (!oldest) return true;
  return oldest >= fromDate;
}

/**
 * Country sidebar: 30-day window, group by date, newest date first. No round filter.
 * @param {object} league
 * @param {{ days?: number, isKetqua?: boolean }} [opts]
 */
export async function fetchMatchesForLeagueByDays(league, { days = 30, isKetqua = false } = {}) {
  const leagueId = league?.id;
  if (leagueId == null || leagueId === "") return [];

  const { fromDate, toDate } = dateWindow(days, isKetqua);

  return withInflight(
    `days:${isKetqua ? "kq" : "ltd"}:${leagueId}:${days}:${fromDate}:${toDate}`,
    async () => {
      const all = [];
      let page = 1;
      let hasMore = true;
      while (hasMore && page <= MAX_PAGES) {
        const result = await fetchMatchesPageByDays(leagueId, page, {
          isKetqua,
          toDate,
        });
        all.push(...(result.list || []));
        hasMore = result.hasMore && pageStillInWindow(result.list, fromDate);
        page += 1;
      }
      const inRange = filterMatchesInDateRange(all, fromDate, toDate);
      const map = groupMatchesByDate(inRange, { withinDayDesc: true });
      return sortDateEntriesDesc(Array.from(map.entries()));
    },
  );
}
